/* ============================================================================
   STOREFRONT OVERLAYS + AJAX CART  —  assets/overlays.js
   Loaded with `defer` from layout/theme.liquid.

   Two jobs:
     1. the menu drawer and the cart drawer (open / close / Esc / scroll lock)
     2. the cart itself, without a page load: add, quantity, remove, re-render

   The cart markup is re-rendered from Liquid on every change via the Section
   Rendering API (`/cart?section_id=cart-drawer`), so every price in the drawer
   is formatted by Shopify and no money maths happens here.

   The checkout button is deliberately OUTSIDE the region this file re-renders.
   Shiprocket's `removeNativeButton` swaps `button[name="checkout"]` for its own
   headless-checkout button; keeping ours out of the re-render is what lets that
   swap survive a quantity change. See the comment on .tpc-dcd__foot.
   ============================================================================ */
(function () {
  'use strict';

  var SECTION_FALLBACK = 'cart-drawer';

  /* ---------------------------------------------------------------- helpers */
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return [].slice.call((root || document).querySelectorAll(sel)); }

  function parseHTML(html) {
    var t = document.createElement('div');
    t.innerHTML = html;
    return t;
  }

  /* One overlay controller, used by both drawers: open/close, Esc, scrim click,
     body scroll lock, aria state, and a plain Tab trap so focus cannot wander
     out the back of a modal. */
  function overlay(el, opts) {
    if (!el) return null;
    var panel = qs(opts.panel, el);
    var lastFocus = null;
    var ctl = {
      isOpen: function () { return el.classList.contains('is-open'); },
      open: function () {
        if (ctl.isOpen()) return;
        lastFocus = document.activeElement;
        el.classList.add('is-open');
        el.setAttribute('aria-hidden', 'false');
        document.body.classList.add('tpc-overlay-open');
        if (opts.onOpen) opts.onOpen();
        if (panel) panel.focus({ preventScroll: true });
      },
      close: function () {
        if (!ctl.isOpen()) return;
        el.classList.remove('is-open');
        el.setAttribute('aria-hidden', 'true');
        if (!document.querySelector('.tpc-dcd.is-open') && !document.querySelector('.tpc-drawer.is-open')) {
          document.body.classList.remove('tpc-overlay-open');
        }
        if (opts.onClose) opts.onClose();
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      },
      toggle: function () { ctl.isOpen() ? ctl.close() : ctl.open(); }
    };

    el.addEventListener('click', function (e) {
      if (e.target.closest(opts.closeSelector)) { e.preventDefault(); ctl.close(); }
    });

    document.addEventListener('keydown', function (e) {
      if (!ctl.isOpen()) return;
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); ctl.close(); return; }
      if (e.key !== 'Tab' || !panel) return;
      var f = qsa('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', panel)
        .filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    return ctl;
  }

  /* ------------------------------------------------------------ menu drawer */
  var menu = overlay(qs('#tpc-menu'), {
    panel: '.tpc-drawer__panel',
    closeSelector: '[data-menu-close]'
  });
  var menuToggle = qs('[data-menu-toggle]');
  if (menuToggle && menu) {
    menuToggle.addEventListener('click', function (e) {
      e.preventDefault();
      menu.toggle();
    });
  }

  /* ------------------------------------------------------------ cart drawer */
  var drawer = qs('[data-cart-drawer]');
  if (!drawer) return;

  var drawerCtl = overlay(drawer, {
    panel: '.tpc-dcd__panel',
    closeSelector: '[data-dcd-close]',
    /* The scratch foil is painted from the stage's measured box, and the drawer is only
       `visibility:hidden` while closed — so the first paint is at the right size — but a font or
       layout shift between then and now would leave it mis-sized. Re-measure on open, for cards
       nobody has touched yet. */
    onOpen: function () { if (window.tpcScratch) window.tpcScratch.resize(drawer); },
    onClose: function () { clearNotice(); }
  });

  var bodyEl = qs('[data-dcd-body]', drawer);
  var sectionId = drawer.getAttribute('data-section-id') || SECTION_FALLBACK;

  function openCart() { if (drawerCtl) drawerCtl.open(); }

  qsa('[data-cart-open]').forEach(function (a) {
    a.addEventListener('click', function (e) {
      e.preventDefault();
      openCart();
    });
  });

  /* A notice line, for the failures Shopify actually reports (sold out, quantity
     limits). It sits at the top of the scroller so it cannot be missed. */
  function notice(msg) {
    clearNotice();
    var p = document.createElement('p');
    p.className = 'tpc-dcd__notice';
    p.setAttribute('data-dcd-notice', '');
    p.textContent = msg;
    if (bodyEl) bodyEl.insertBefore(p, bodyEl.firstChild);
  }
  function clearNotice() {
    qsa('[data-dcd-notice]', drawer).forEach(function (n) { n.parentNode.removeChild(n); });
  }

  function setCount(n) {
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      if (n > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });
    qsa('[data-dcd-count]', drawer).forEach(function (el) { el.textContent = n; });
    qsa('[data-cart-open]').forEach(function (a) {
      a.setAttribute('aria-label', n > 0 ? 'Cart, ' + n + (n === 1 ? ' item' : ' items') : 'Cart');
    });
    if (n === 0) drawer.classList.add('is-empty'); else drawer.classList.remove('is-empty');
  }

  /* Replace only the scroller. The head and the foot — including the checkout anchor — are left
     exactly where they are, so nothing the customer is pointing at moves under the thumb. */
  function applySection(html) {
    var doc = parseHTML(html);
    var newBody = qs('[data-dcd-body]', doc);
    if (!newBody || !bodyEl) return false;
    var keepScroll = bodyEl.scrollTop;       /* a quantity change must not scroll the list away */
    /* The scratch card is rebuilt with the rest of the body, so its foil and its bindings go too.
       Carry a completed reveal across: the scratch IS the gift, and re-covering it whenever somebody
       nudged a quantity would read as a broken card rather than a fresh one. */
    var scratchOpen = !!qs('.tpc-scratch.is-open', bodyEl);
    bodyEl.innerHTML = newBody.innerHTML;
    bodyEl.scrollTop = keepScroll;
    if (window.tpcScratch) {
      window.tpcScratch.init(bodyEl);
      if (scratchOpen) {
        var fresh = qs('[data-scratch]', bodyEl);
        if (fresh && fresh.__tpcScratch) fresh.__tpcScratch.open();
      }
    }
    var total = qs('[data-dcd-total-src]', doc);
    if (total) {
      var v = total.textContent.trim();
      qsa('[data-dcd-total]').forEach(function (el) { el.textContent = v; });
    }
    var c = qs('[data-dcd-count]', doc);
    if (c) setCount(parseInt(c.textContent.trim(), 10) || 0);
    return true;
  }

  function fetchSection() {
    return fetch(themedUrl('/cart?section_id=' + encodeURIComponent(sectionId)), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        if (applySection(html)) return true;
        /* the id the layout stamped did not resolve — fall back to the file name */
        if (sectionId === SECTION_FALLBACK) return false;
        return fetch(themedUrl('/cart?section_id=' + SECTION_FALLBACK), { credentials: 'same-origin' })
          .then(function (r) { return r.text(); })
          .then(function (h2) { return applySection(h2); });
      });
  }

  /* Previewed themes live behind `?preview_theme_id=`, and that parameter is not carried by a fetch
     on its own — without this the drawer would quietly re-render itself from the PUBLISHED theme
     while somebody is looking at a preview, which is the one place it is most likely to be looked
     at. Mutations (/cart/add.js, /cart/change.js) are theme-independent and are left alone. */
  function themedUrl(path) {
    var id = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (!id) return path;
    return path + (path.indexOf('?') === -1 ? '?' : '&') + 'preview_theme_id=' + encodeURIComponent(id);
  }

  function post(url, data) {
    var body = new URLSearchParams();
    Object.keys(data).forEach(function (k) { body.append(k, data[k]); });
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
      body: body.toString()
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok) {
          var msg = (j && (j.description || j.message)) || 'That did not go through. Please try again.';
          throw new Error(msg);
        }
        return j;
      });
    });
  }

  /* ------------------------------------------------------- add from the PDP */
  function busy(btn, on) {
    if (!btn) return;
    if (on) { btn.setAttribute('aria-busy', 'true'); btn.classList.add('is-disabled'); }
    else { btn.removeAttribute('aria-busy'); btn.classList.remove('is-disabled'); }
  }

  function addVariant(id, qty, btn) {
    busy(btn, true);
    return post('/cart/add.js', { id: id, quantity: qty })
      .then(function () { return fetchSection(); })
      .then(function () { openCart(); })
      .catch(function (err) { notice(err.message); openCart(); })
      .then(function () { busy(btn, false); });
  }

  /* THE PACK UPGRADE REPLACES, IT DOES NOT STACK.

     There is no atomic variant swap on the Cart API: `POST /cart/change.js` carrying `line` AND `id`
     is rejected outright — 400, "no valid id or line parameter" — and `id` on its own the same way.
     (Verified against this store, 1 Oct.) So the swap is two calls: ADD the bigger pack, then take
     the pack it replaces down to zero.

     The order is add-then-remove so the cart is never briefly empty, but that means the line index
     captured in Liquid is STALE by the second call — whether an add appends or prepends is Shopify's
     business, not ours. So the index is re-read from /cart.js after the add and the replaced pack is
     removed where it actually sits now. If it is no longer there, nothing is removed and the customer
     simply keeps the bigger pack. */
  function swapVariant(btn) {
    var to = btn.getAttribute('data-dcd-swap');
    var from = btn.getAttribute('data-dcd-swap-from');
    var qty = parseInt(btn.getAttribute('data-dcd-swap-qty'), 10) || 1;
    busy(btn, true);

    post('/cart/add.js', { id: to, quantity: qty })
      .then(function () {
        return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        if (String(to) === String(from)) return 0;
        for (var i = 0; i < items.length; i++) {
          if (String(items[i].id) === String(from)) return i + 1;   /* /cart/change.js lines are 1-based */
        }
        return 0;
      })
      .then(function (idx) {
        return idx ? post('/cart/change.js', { line: idx, quantity: 0 }) : null;
      })
      .then(function () { return fetchSection(); })
      .catch(function (err) { return fetchSection().then(function () { notice(err.message); }); })
      .then(function () { busy(btn, false); });
  }

  /* Every `form[action*="/cart/add"]` in the theme — the buy box today, anything
     added later — adds over the wire and opens the drawer instead of navigating.
     The body is built explicitly rather than by handing over the FormData: the
     buy box also carries `pack_choice`, `data-*` driven quantity and the pack
     radios, and none of those are cart parameters Shopify should receive. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.nodeName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    if (form.hasAttribute('data-dcd-skip')) return;

    var idField = qs('[name="id"]', form);
    if (!idField || !idField.value) return;   /* let the browser submit and report */
    e.preventDefault();

    var qtyField = qs('[name="quantity"]', form);
    var btn = qs('button[type="submit"], input[type="submit"]', form);
    addVariant(idField.value, (qtyField && qtyField.value) || 1, btn);
  });

  /* --------------------------------------------- quantity / remove / upsell */
  /* Every change is an absolute quantity, so overlapping requests cannot fight: the reply that is
     applied is the one from the newest request, and older replies are dropped. The earlier
     `pending` flag this replaces simply swallowed a second tap, which reads as a dead button. */
  var seq = 0;

  function changeLine(line, quantity) {
    var mine = ++seq;
    post('/cart/change.js', { line: line, quantity: quantity })
      .then(function () { if (mine === seq) return fetchSection(); })
      .catch(function (err) {
        if (mine !== seq) return;
        /* re-sync FIRST, then report: the refetch replaces the scroller, so a notice written before
           it would be wiped a moment later and the failure would look like nothing happened. */
        return fetchSection().then(function () { notice(err.message); });
      });
  }

  drawer.addEventListener('click', function (e) {
    var step = e.target.closest('[data-dcd-qty] button[data-step]');
    if (step) {
      var wrap = step.closest('[data-dcd-qty]');
      var input = qs('input', wrap);
      var line = input.getAttribute('data-line');
      var next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-step'), 10));
      input.value = next;
      changeLine(line, next);
      return;
    }

    var rm = e.target.closest('[data-dcd-remove]');
    if (rm) { changeLine(rm.getAttribute('data-line'), 0); return; }

    var swap = e.target.closest('[data-dcd-swap]');
    if (swap) { swapVariant(swap); return; }
  });

  /* A typed quantity applies on blur or Enter, once the value has settled. */
  drawer.addEventListener('change', function (e) {
    var input = e.target.closest('[data-dcd-qty-input]');
    if (!input) return;
    var n = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(input.getAttribute('data-line'), n);
  });

  /* ---------------------------------------------------------------- checkout */
  /* The anchor already points at /cart, which is a working route to the store's checkout on its own
     (and is what a customer gets with JS off). When Shiprocket's script is on the page, hand the
     click to the very function their own generated button calls, so the drawer reaches the same
     headless checkout the cart page does instead of stopping one page short of it.

     The control deliberately does NOT carry `name="checkout"` or `href="/checkout"`: their
     `removeNativeButton` rewrites every element matching those, by moving a single node into each
     match — with the /cart page's own button in that list as well, a second match on the same page
     would leave one of the two empty. See the comment on .tpc-dcd__foot. */
  var checkout = qs('[data-dcd-checkout]', drawer);
  if (checkout) {
    checkout.addEventListener('click', function (e) {
      var sr = window.shiprocketCheckoutEvents;
      if (!sr || typeof sr.buyCart !== 'function') return;   /* let href="/cart" run */
      e.preventDefault();
      try {
        sr.buyCart(e);
      } catch (err) {
        window.location.href = checkout.getAttribute('href') || '/cart';
      }
    });
  }
})();
